import React, { useState } from 'react';
import { Card, Row, Col, Typography, Tooltip, Tag, Button, Collapse } from 'antd';
import { InfoCircleOutlined, ExperimentOutlined, HistoryOutlined, DownOutlined } from '@ant-design/icons';
import moment from 'moment';
import LabHistoryModal from './LabHistoryModal';

const { Text, Title } = Typography;
const { Panel } = Collapse;

// Helper function to safely extract value from potential object
const getLabValue = (lab) => {
  if (lab && typeof lab === 'object') {
    let extractedValue = lab.value !== undefined ? lab.value : undefined;
    
    if (extractedValue && typeof extractedValue === 'object' && extractedValue.value !== undefined) {
      extractedValue = extractedValue.value;
    }
    
    return extractedValue;
  }
  return lab;
};

// Helper function to get lab category from parameter name
const getLabCategory = (parameterName) => {
  if (!parameterName) return 'Other';

  const categoryMap = {
    'labs_cbc_': 'Complete Blood Count (CBC)',
    'labs_cmp_': 'Comprehensive Metabolic Panel (CMP)',
    'labs_lipid_': 'Lipid Panel',
    'labs_liver_': 'Liver Function Tests',
    'labs_thyroid_': 'Thyroid Function',
    'labs_glucose_': 'Glucose/Diabetes',
    'labs_cardiac_': 'Cardiac Markers',
    'labs_coag_': 'Coagulation Studies',
    'labs_inflammatory_': 'Inflammatory Markers',
    'labs_renal_': 'Renal Function',
    'labs_electrolytes_': 'Electrolytes',
    'labs_vitamins_': 'Vitamins & Nutrients',
    'labs_hormones_': 'Hormones',
    'labs_tumor_': 'Tumor Markers',
    'labs_infectious_': 'Infectious Disease',
    'labs_immunology_': 'Immunology',
    'labs_hematology_': 'Hematology',
    'labs_chemistry_': 'Chemistry',
    'labs_microbiology_': 'Microbiology',
    'labs_pathology_': 'Pathology'
  };

  // Find matching category
  for (const [prefix, category] of Object.entries(categoryMap)) {
    if (parameterName.startsWith(prefix)) {
      return category;
    }
  }

  return 'Other Labs';
};

// Helper function to get display name for lab parameter
const getLabDisplayName = (parameterName) => {
  const labDisplayNames = {
    'labs_lipid_chol_total': 'Total Cholesterol',
    'labs_lipid_hdl': 'HDL Cholesterol',
    'labs_lipid_ldl': 'LDL Cholesterol',
    'labs_lipid_trig': 'Triglycerides',
    'labs_glucose_glucose': 'Glucose',
    'labs_glucose_hba1c': 'HbA1c',
    'labs_cbc_wbc': 'White Blood Cells',
    'labs_cbc_rbc': 'Red Blood Cells',
    'labs_cbc_hgb': 'Hemoglobin',
    'labs_cbc_hct': 'Hematocrit',
    'labs_cbc_plt': 'Platelets',
    'labs_cmp_sodium': 'Sodium',
    'labs_cmp_potassium': 'Potassium',
    'labs_cmp_chloride': 'Chloride',
    'labs_cmp_co2': 'CO2',
    'labs_cmp_bun': 'BUN',
    'labs_cmp_creatinine': 'Creatinine',
    'labs_cmp_glucose': 'Glucose',
    'labs_liver_alt': 'ALT',
    'labs_liver_ast': 'AST',
    'labs_liver_alp': 'Alkaline Phosphatase',
    'labs_liver_bilirubin_total': 'Total Bilirubin',
    'labs_thyroid_tsh': 'TSH',
    'labs_thyroid_t4_free': 'Free T4',
    'labs_thyroid_t3_free': 'Free T3'
  };
  
  return labDisplayNames[parameterName] || parameterName.replace(/^labs_/, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Helper function to get flag color
const getFlagColor = (flag) => {
  if (!flag) return 'default';
  
  const upperFlag = flag.toString().toUpperCase();
  switch (upperFlag) {
    case 'H':
    case 'HIGH':
      return 'orange';
    case 'L':
    case 'LOW':
      return 'blue';
    case 'C':
    case 'CRITICAL':
      return 'red';
    case 'A':
    case 'ABNORMAL':
      return 'orange';
    default:
      return 'default';
  }
};

// Helper function to format lab for display
const formatLabForDisplay = (lab, onViewHistory) => {
  const value = getLabValue(lab.value);
  const displayName = getLabDisplayName(lab.name);

  if (value === undefined || value === null) {
    return null;
  }

  // Determine if current value is abnormal and get the flag
  const flag = lab.flag ? lab.flag.toUpperCase() : '';
  const isCurrentlyAbnormal = ['H', 'L', 'HIGH', 'LOW', 'A', 'ABNORMAL', 'C', 'CRITICAL'].includes(flag);
  const valueColor = '#d4380d'; // Always red since we're only showing abnormal values

  // Determine high/low status for clearer indication
  const isHigh = ['H', 'HIGH'].includes(flag);
  const isLow = ['L', 'LOW'].includes(flag);
  const isCritical = ['C', 'CRITICAL'].includes(flag);

  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <Text strong style={{ fontSize: '14px' }}>{displayName}: </Text>
          <Text style={{ fontSize: '14px', color: valueColor, fontWeight: 'bold' }}>
            {value} {lab.units || ''}
          </Text>
          {lab.flag && (
            <Tag
              color={getFlagColor(lab.flag)}
              size="small"
              style={{ marginLeft: '8px', fontWeight: 'bold' }}
            >
              {isHigh ? 'HIGH' : isLow ? 'LOW' : isCritical ? 'CRITICAL' : lab.flag}
            </Tag>
          )}
          {/* Show how far out of range */}
          {lab.abnormalDetails && (
            <Tooltip title={lab.abnormalDetails}>
              <Tag
                color={isCritical ? 'red' : isHigh ? 'orange' : 'blue'}
                size="small"
                style={{ marginLeft: '4px' }}
              >
                {isHigh ? '↑' : isLow ? '↓' : '!'}
              </Tag>
            </Tooltip>
          )}
        </div>
        <Button
          type="text"
          size="small"
          icon={<HistoryOutlined />}
          onClick={() => onViewHistory(lab.name)}
          style={{
            padding: '2px 4px',
            height: 'auto',
            fontSize: '12px'
          }}
          title="View lab history"
        />
      </div>
      <div style={{ marginTop: '4px' }}>
        <Text style={{ fontSize: '12px', color: '#595959', fontWeight: '500' }}>
          Normal Range: {lab.referenceRange || 'N/A'}
        </Text>
        <br />
        <Text type="secondary" style={{ fontSize: '11px' }}>
          Most Recent: {lab.entryDate ? moment(lab.entryDate).format('M/D/YYYY') : 'No date'}
        </Text>
      </div>
    </div>
  );
};

const AbnormalLabsDisplay = ({
  abnormalLabsData,
  isEncounterSigned = false,
  allLabEntries = [] // All lab entries for history modal
}) => {
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState(null);

  if (!abnormalLabsData || abnormalLabsData.length === 0) {
    return <Text type="secondary">No current abnormal lab values.</Text>;
  }

  const handleViewHistory = (parameterName) => {
    setSelectedParameter(parameterName);
    setHistoryModalVisible(true);
  };

  const handleCloseHistory = () => {
    setHistoryModalVisible(false);
    setSelectedParameter(null);
  };

  // Sort labs by date (most recent first)
  const sortedLabs = [...abnormalLabsData]
    .sort((a, b) => new Date(b.entryDate) - new Date(a.entryDate));

  // Group all labs by category
  const labsByCategory = sortedLabs.reduce((acc, lab) => {
    const category = getLabCategory(lab.name);
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(lab);
    return acc;
  }, {});

  const cardTitle = isEncounterSigned ? 'Abnormal Labs (Snapshot)' : 'Current Abnormal Labs';
  const titleColor = isEncounterSigned ? '#722ed1' : '#d4380d';
  const iconColor = isEncounterSigned ? '#722ed1' : '#d4380d';
  const cardStyle = isEncounterSigned ? { borderColor: '#722ed1', borderWidth: '2px' } : { borderColor: '#d4380d', borderWidth: '1px' };
  const tooltipText = isEncounterSigned
    ? 'This is a snapshot of abnormal labs taken when the encounter was signed'
    : 'Current laboratory values that are outside their normal reference ranges';

  // Helper function to render labs by category
  const renderLabsByCategory = (labsByCategory) => {
    return Object.entries(labsByCategory).map(([category, labs]) => (
      <div key={category} style={{ marginBottom: '16px' }}>
        <Text strong style={{ fontSize: '13px', color: '#1890ff', marginBottom: '8px', display: 'block' }}>
          {category} ({labs.length})
        </Text>
        <Row gutter={[16, 8]}>
          {labs.map((lab, index) => (
            <Col key={index} xs={24} sm={12}>
              {formatLabForDisplay(lab, handleViewHistory)}
            </Col>
          ))}
        </Row>
      </div>
    ));
  };

  return (
    <>
    <Card
      size="small"
      title={
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={5} style={{ margin: 0, color: titleColor }}>
              <ExperimentOutlined style={{ marginRight: 8 }} />
              {cardTitle}
            </Title>
          </Col>
          <Col>
            <Tooltip title={tooltipText}>
              <InfoCircleOutlined style={{ color: iconColor }} />
            </Tooltip>
          </Col>
        </Row>
      }
      style={cardStyle}
    >
      {/* Summary information */}
      <div style={{ marginBottom: '12px', textAlign: 'center' }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {sortedLabs.length} lab parameter{sortedLabs.length > 1 ? 's' : ''} currently out of normal range
        </Text>
      </div>

      {/* Expandable section for all abnormal labs */}
      <Collapse
        ghost
        size="small"
        expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
      >
        <Panel
          header={
            <Text style={{ fontSize: '13px', fontWeight: 'bold' }}>
              View All Abnormal Labs ({sortedLabs.length})
            </Text>
          }
          key="1"
        >
          {renderLabsByCategory(labsByCategory)}

          <div style={{ marginTop: '12px', textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: '10px' }}>
              Click <HistoryOutlined style={{ fontSize: '10px' }} /> to view historical trends
            </Text>
          </div>
        </Panel>
      </Collapse>
    </Card>

    {/* Lab History Modal */}
    <LabHistoryModal
      visible={historyModalVisible}
      onClose={handleCloseHistory}
      parameterName={selectedParameter}
      labEntries={allLabEntries}
    />
  </>
  );
};

export default AbnormalLabsDisplay;
